import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { useLazyGetActivePracticeAreasWithFiltersQuery } from 'store/api/practice-areas';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { MinDto } from 'types/other';
import { PracticeAreaType } from 'types/practice-areas';
import { TeamType } from 'types/teams';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { ApplyExclusionToSubareasField } from './ApplyExclusionToSubareasField';
import { PracticeAreaFormState } from './usePracticeAreaForm';

/** The type, name, parent, practice group with its teams, AML, automatic-closing exclusion and active. */
export const PracticeAreaFields = ({ form }: { form: PracticeAreaFormState }): JSX.Element => {
    const { id: practiceAreaId } = useCreateRoute().params;
    const { control, setValue } = form.methods;
    const { isSubPracticeAreaFeatureEnabled } = form;
    // On the record page the teams are drawn as the card's read row draws them, links included.
    const inSection = useInRecordSection();
    return (
        <>
            <RadioInput
                setValue={setValue}
                control={control}
                label="practice-areas.practice-area-type"
                name="practiceAreaType"
                options={form.practiceAreaTypeOptions}
                getOptionLabel="label"
                getOptionIdentifier="value"
                // Read-only while sub-practice areas are disabled.
                disabled={!isSubPracticeAreaFeatureEnabled}
            />
            <TextInput
                control={control}
                label="practice-areas.name"
                placeholder="practice-areas.enter-name"
                name="name"
                required
            />
            {form.practiceAreaType === PracticeAreaType.PRACTICE_SUBAREA && isSubPracticeAreaFeatureEnabled ? (
                <DynamicSelectInput
                    setValue={setValue}
                    control={control}
                    label="practice-areas.parent-practice-area"
                    placeholder="practice-areas.select-parent-practice-area"
                    name="parent"
                    getOptionLabel={(option) => formatPracticeAreaLabel(option)}
                    getOptionIdentifier="id"
                    required
                    paginated={{
                        fetch: useLazyGetActivePracticeAreasWithFiltersQuery,
                        filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}${practiceAreaId ? `;id!=${Number(practiceAreaId)}` : ''}`,
                        searchField: 'name',
                    }}
                />
            ) : null}
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="practice-areas.practice-group"
                placeholder="practice-areas.select-practice-group"
                name="practiceGroup"
                paginated={{
                    fetch: useLazyGetMinTeamsListWithFiltersQuery,
                    filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
                    searchField: 'name',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <MultiSelectInput
                setValue={setValue}
                control={control}
                label="practice-areas.practice-teams"
                placeholder="practice-areas.select-practice-teams"
                name="practiceTeams"
                options={[] as MinDto[]}
                getOptionLabel="name"
                getOptionIdentifier="id"
                readonly={inSection ? { linkTo: 'team' } : true}
            />
            <CheckboxInput control={control} name="isAmlApplicable" label="practice-areas.aml-applicable" />
            <div className="flex flex-col gap-2">
                <CheckboxInput
                    control={control}
                    name="isExcludedFromAutomaticClosing"
                    label="case-closing-practice-area.excluded-from-automatic-closing"
                />
                <ApplyExclusionToSubareasField form={form} />
            </div>
            <CheckboxInput control={control} name="isActive" label="active" />
        </>
    );
};

/** Where the practice area is offered: timecards, cases and employees. */
export const PracticeAreaVisibilityFields = ({ form }: { form: PracticeAreaFormState }): JSX.Element => {
    const { control } = form.methods;
    return (
        <>
            <CheckboxInput control={control} name="showInTimecards" label="practice-areas.show-in-timecards" />
            <CheckboxInput control={control} name="showInCases" label="practice-areas.show-in-cases" />
            <CheckboxInput control={control} name="showInEmployees" label="practice-areas.show-in-employees" />
        </>
    );
};
